import apolloClient from 'src/data/api';
import { Loading, useGetOne, useNotify, useRecordContext, useRedirect } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { organizationEditRoute, OrganizationEditTab } from 'src/utils/routeHelpers';
import CraftTabbedForm from 'src/components/CraftTabbedForm';
import { OrganizationRecord, PassportRecord, PassportScopeLabel } from 'src/types';
import { usePassportScopes } from './usePassportScopes';
import { UPSERT_PASSPORT_MUTATION, buildUpsertPassportObject, DELETE_PASSPORT_MUTATION } from './helpers';
import { PassportFormData, SubscriptionSetMapping } from './formTabs/types';
import PassportGeneral from './formTabs/PassportGeneral';
import PassportScopeMapping from './formTabs/PassportScopeMapping';
import SubscriptionSetMappings from './formTabs/SubscriptionSetMappings';

export const initMappingsFromPassport = (passport: PassportRecord): SubscriptionSetMapping[] =>
  passport.passport_subscription_set_scopes.map((scope) => ({
    subscriptionSetId: String(scope.subscription_set_id),
    scope: scope.scope_key ?? 'NONE',
    participantOrgIds: (scope.participants ?? []).map((p) => p.participant_org_id),
  }));

const PassportForm = ({ formType }: { formType: 'create' | 'edit' }) => {
  const isEdit = formType === 'edit';
  const record = useRecordContext<PassportRecord>();
  const notify = useNotify();
  const redirect = useRedirect();
  const { scopeKeys, isLoading: scopesLoading } = usePassportScopes();
  const [searchParams] = useSearchParams();
  const fromOrgId = searchParams.get('fromOrgId') || '';
  const { data: org } = useGetOne<OrganizationRecord>(
    'organizations',
    { id: Number(fromOrgId) },
    { enabled: !isEdit && !!fromOrgId },
  );

  const companyDataApiKey = isEdit ? record?.authority?.company_data_api_key : org?.company_data_api_key;

  const goBack = () => {
    if (fromOrgId) {
      redirect(organizationEditRoute(fromOrgId, OrganizationEditTab.PASSPORTS));
    } else {
      redirect('/organizations');
    }
  };

  const handleSubmit = async (formValues: Record<string, unknown>) => {
    const values = formValues as unknown as PassportFormData;

    if (!values.name?.trim()) {
      notify('Name is required', { type: 'error' });
      return;
    }

    const authorityOrgId = isEdit ? record?.authority_org_id : org?.guid;
    if (!authorityOrgId) {
      notify('Organization not found', { type: 'error' });
      return;
    }

    try {
      const object = buildUpsertPassportObject(values, isEdit ? record : undefined, authorityOrgId);
      await apolloClient.mutate({
        mutation: UPSERT_PASSPORT_MUTATION,
        variables: { object },
      });
      notify(`Passport ${isEdit ? 'updated' : 'created'} successfully`, { type: 'success' });
      goBack();
    } catch (e) {
      console.error(e);
      notify(`Error ${isEdit ? 'updating' : 'creating'} passport`, { type: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    try {
      await apolloClient.mutate({
        mutation: DELETE_PASSPORT_MUTATION,
        variables: { id: record.id },
      });
      notify('Passport deleted successfully', { type: 'success' });
      goBack();
    } catch (e) {
      console.error(e);
      notify('Error deleting passport', { type: 'error' });
    }
  };

  const defaultValues: PassportFormData =
    isEdit && record
      ? (() => {
          const scopeLabels = {} as PassportFormData['scopeLabels'];
          const passportScopes = record.passport_scope_labels.reduce((acc, label) => {
            acc[label.scope_key] = label;
            return acc;
          }, {} as Record<string, PassportScopeLabel>);

          for (const scopeKey of scopeKeys) {
            const existing = passportScopes[scopeKey];
            scopeLabels[scopeKey] = {
              displayLabel: existing?.display_label ?? '',
              displayDescription: existing?.display_description ?? '',
            };
          }

          return {
            name: record.name,
            description: record.description ?? '',
            scopeLabels,
            subscriptionSetMappings: initMappingsFromPassport(record),
          };
        })()
      : {
          name: '',
          description: '',
          scopeLabels: {} as PassportFormData['scopeLabels'],
        };

  if (scopesLoading) {
    return (
      <Box py={12} px={8}>
        <Loading />
      </Box>
    );
  }

  return (
    <CraftTabbedForm
      formType={formType}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      deleteOptions={
        isEdit
          ? {
              deletePermission: 'organization:edit',
              onConfirmDelete: handleDelete,
              dialogTitle: `Delete passport "${record?.name}"`,
              dialogContent: 'Are you sure you want to delete this passport? This action cannot be undone.',
            }
          : { hideDeleteButton: true, deletePermission: 'organization:edit' }
      }
      mutationMode="pessimistic"
    >
      <PassportGeneral />
      <PassportScopeMapping />
      <SubscriptionSetMappings companyDataApiKey={companyDataApiKey} />
    </CraftTabbedForm>
  );
};

export default PassportForm;
